import { roleSchema } from './user.js';
/**
 * AD-36's first layer, as data: what each role may do, written once for both sides of the wire.
 *
 * **It lives in the contracts package because two programs need the same answer.** The API is
 * authoritative and checks every route twice regardless of what any client believes; the browser
 * needs the same table to decide which controls to OFFER, since showing an operator a "destroy this
 * record" action that answers 403 is a worse interface than not showing it. A second table in the
 * browser is the shape that mistake takes, and it fails in the direction nobody notices: a verb
 * added to the server's table stays invisible on every screen, or worse, a stale copy keeps offering
 * an action the server stopped granting. `packages/server/src/auth/roles.ts` re-exports these, so
 * the server's own modules still import the table from where the route layer lives.
 *
 * The rule this file exists to enforce is that a role check never appears as a condition inside a
 * handler. A condition is invisible: nobody can answer "what can an operator reach?" by reading
 * forty handlers, and nobody notices when the forty-first forgets to ask. A table can be printed,
 * reviewed, and iterated by a test that fails when a route joins the system without a row.
 *
 * **A permission is a verb on a subject, not a route.** Several routes share one permission —
 * reading a person, reading their images and reading their revisions are all `person:read` — and a
 * permission outlives the URL it is reached through. That is what lets the server's route registry
 * grow a route without growing this table, and it is what makes the matrix reviewable at all: the
 * interesting question is which VERBS a role holds, and there are a few dozen of those against a few
 * hundred route-times-role cells.
 *
 * **The separator is a colon, and it is load-bearing rather than a style choice.** Audit actions in
 * the server's `audit/service.ts` are dotted — `person.shred`, `credential.issue` — and several of
 * them name the same subject and verb as a permission here. With one separator the two closed sets
 * would be textually identical in places, and the audit catalogue's own status scan, which reads the
 * server sources looking for an action's literal, would read a PERMISSION as evidence that somebody
 * emits that ACTION. It found exactly that when this table used dots, and marked six reserved
 * actions as having callers they do not have. A reader gets the same benefit the scan does:
 * `person:shred` is a thing somebody is allowed to do, and `person.shred` is a thing that happened.
 *
 * **The grant table is total over the permission set, by the type system.** `satisfies` on the
 * literal below means a permission added without a row is a compile error rather than a permission
 * nobody holds, which would fail closed but silently: the feature would simply never work, in
 * production, for everyone, and the first report would come from a user.
 *
 * **Roles are not a ladder in this table, deliberately.** `ROLE_RANK` orders them, and it is the
 * right tool for "is this actor at least an admin". It is the wrong tool for a grant, because a
 * rank-based grant makes every future permission implicitly held by everyone above the rank that was
 * named, including permissions nobody meant to hand out. Each row therefore names its roles outright,
 * and the rows that read as a ladder read that way because somebody wrote them that way.
 */
/** Every verb this system authorizes, grouped by the subject it acts on. */
export const PERMISSIONS = [
    // The registry (Phases 12, 17).
    'person:read',
    'person:read_full',
    'person:write',
    'person:status',
    'person:shred',
    'person:export',
    'person:import',
    // The field registry (AD-16, AD-17).
    'field:read',
    'field:write',
    'field:purge',
    // Passes (Phases 12A, 13).
    'credential:read',
    'credential:reveal',
    'credential:issue',
    'credential:revoke',
    'credential:print',
    'credential_batch:read',
    'credential_batch:write',
    // The gate (Phase 14).
    'presence:read',
    'presence:submit',
    'presence:override',
    'presence:supervise',
    'presence:backfill',
    'presence:investigate',
    // The counter (dining).
    'dining:serve',
    'dining:override',
    'dining:read',
    // Reporting.
    'analytics:read',
    'analytics:read_security',
    'audit:read',
    'alert:read',
    'alert:acknowledge',
    // Administration.
    'user:read',
    'user:write',
    'user:reset_credentials',
    'setting:read',
    'setting:write',
    'api_key:read',
    'api_key:write',
    'backup:read',
    'backup:run',
    'backup:restore',
    'backup:purge',
    'export:read',
    'export:create',
    'system:read',
    'system:maintain',
    // The actor's own account: changing their own password, setting their own PIN, enrolling their
    // own authenticator, re-authenticating an idle-locked terminal. Held by every role, because a
    // role that could not change its own password would be a role that could never satisfy the
    // forced first-login change.
    'self:manage',
];
/**
 * Who holds each verb.
 *
 * `super_admin` is not implicitly everything, and the rows it holds alone are two families rather
 * than one. The first is the IRREVERSIBLE operations — `person:shred`, `field:purge`,
 * `backup:restore`, `backup:purge` — where a mistake cannot be undone by anybody. The second is the
 * INSTALLATION'S OWN CONFIGURATION: the field registry's writes, every setting, the machine
 * credentials, the pre-printed band batches and the archives. Every other row names `super_admin`
 * explicitly too, so that a reader never has to know a fallback rule to predict the answer.
 *
 * **`person:import` stands with them for a third reason.** Importing the intake spreadsheet writes
 * hundreds of records in one sitting, every one of them inside the building, and undoing it is a
 * record-by-record erasure. It is a migration the installation performs once or twice, not a thing
 * a desk does, so it sits with the one person who already holds the installation's configuration.
 *
 * **Why the second family is one decision rather than nine.** Each of those verbs opens a screen
 * that changes what the installation IS rather than what it holds: which questions the register
 * asks, how long somebody may be outside, which machines may call in, which bands exist, and
 * whether there is a way back. An administrator runs the facility — they register people, work the
 * gate, correct the record, supervise operators and read every trail — and none of that needs any
 * of these. Concentrating them costs an administrator nothing they do daily and means the set of
 * people who can silently change the rules is exactly one. The screens follow the verbs with no
 * second table: `packages/web/src/features/admin/nav.ts` reads this file.
 *
 * **`user:read`, `user:write` and `user:reset_credentials` are deliberately NOT in that family.**
 * An administrator still administers OPERATORS — the staff turnover a facility actually has — and
 * `user:read` additionally resolves an actor id into a name on the audit trail, the incidents
 * screen and three analytics panels, so narrowing it would print "unknown" across screens an
 * administrator keeps. What stops an administrator reaching another administrator is not this table
 * but the rank rules beside it: `permitUserAdministration` refuses an equal rank as an OBJECT, and
 * `permitRoleAssignment` refuses a role the actor does not outrank as an ASSIGNMENT. A grant is
 * categorical and those two questions are comparative, which is why they are not rows here.
 *
 * Three rows are worth reading twice.
 *
 * `person:read_full` is separate from `person:read` because AD-37 counts full-record views per
 * operator per day and raises the exfiltration alert on them. A single `person:read` would make
 * the threshold uncountable, since a list page and a whole record would be the same event.
 *
 * `presence:supervise` is the verb that EMPTIES THE BUILDING, and nothing else: it gates
 * `POST /api/v1/presence/force-exit-all`, which rewrites the presence state of every person in the
 * installation in one act. An operator does not hold it, for the reason they do not hold
 * `person:shred`. It is deliberately NOT what authorises a blocked person's movement at a gate:
 * `presence:override` is, an operator holds it, and the reason code is what makes the override
 * explicable afterwards — refusing one at a door means a person does not get in while somebody
 * goes looking for an administrator.
 *
 * `dining:serve` and `dining:override` are held by every role that works the counter, for the reason
 * `presence:override` is: a refusal at a food counter with nobody authorised to lift it means a
 * person does not eat while somebody goes looking for an administrator. The override is not ungoverned by being widely held,
 * because what makes a second helping explicable afterwards is the REASON CODE the column requires,
 * which lands permanently on an append-only row. No route checks `dining:override` today, and the
 * submit route records why: every role that reaches it holds it, so the check would be a branch no
 * request can enter, and the day the grants diverge the check belongs on the reason code, which by
 * `meal_events_override_reason_ck` gates the override with nothing left over.
 *
 * `dining:read` is the one of the three that an operator does NOT hold, and the asymmetry is the
 * point. What it opens is the roster, which lists PEOPLE BY NAME against what they have eaten, and
 * there is no operator-facing dining read at all: the counter's whole answer arrives inside the
 * scan, under `presence:submit`. Granting it to operators would be a verb with no screen behind it
 * for the role holding it. It is also separate from `analytics:read` rather than folded into it,
 * because no analytics route in this product names a person and this one does.
 *
 * `presence:investigate` is the verb that OPENS THE PROSE an operator wrote about a person. A
 * reason note is sealed under that person's own data key precisely because it is one human's
 * sentence about another, so reading one back is a disclosure of the same class as revealing a pass
 * code or building a subject-access file — which is why no operator holds it, why no API-key scope
 * names it, and why it is separate from `presence:read`, which an operator holds so they can work a
 * door. Writing a note stays with every operator; reading other people's is for the roles that
 * review the record: an administrator, and an analyst, whose whole job is reading it.
 *
 * Both `credential_batch` verbs are super-admin only because a batch artifact is a list of live
 * bearer tokens (AD-63), which makes it the richest bulk-exfiltration target in the system — and
 * because READING the batch inventory is how you learn which artifact to ask for, the read is held
 * as tightly as the write.
 *
 * `credential:reveal` is separate from `credential:read` for the reason `person:read_full` is
 * separate from `person:read`: reading a credential is reading its status, its type and when it was
 * issued, and revealing one puts a LIVE BEARER TOKEN on a screen in cleartext. An operator does not
 * hold it, and that costs them nothing they need — a minted code is handed back once at issue time,
 * and in a pre-printed installation the code is printed on the band already in the holder's hand, so
 * a later re-disclosure is an administrative act rather than a desk one. It is the same disclosure
 * AD-63 keeps admin-only in bulk, in units of one.
 *
 * `backup:purge` is separate from `backup:run` and from `backup:restore`, and it is super-admin
 * only for the reason `person:shred` is. Section 1.14 item 2 says an erasure's latency equals the
 * archive retention window, because a shredded person's wrapped key survives inside every archive
 * taken before the shred; the answer is to destroy the archives, which also destroys the
 * installation's own recovery path. Folding it into `backup:run` would put "delete every archive"
 * behind the verb that takes one.
 *
 * `field:purge` is separate from `field:write` and is **super-admin only**, for the same reason
 * `person:shred` is. Retiring a field is a reversible administrative act an admin may perform; a
 * purge destroys that field's stored value for EVERY person in the installation and cannot be undone.
 * Folding it into `field:write` would hand an admin an irreversible bulk deletion behind the same
 * permission that renames a label, which is the one thing separating the two exists to prevent.
 *
 * **`analyst` reads and does not act, and its rows are the whole statement of that.** It holds the
 * register in full (`person:read`, `person:read_full`, `field:read`, `credential:read`), the
 * building's state (`presence:read`), the trails a reviewer reads (`presence:investigate`,
 * `dining:read`, `audit:read`, `alert:read`, `analytics:read`, and `user:read`, which is what
 * turns an actor id into a name on those screens), its own account (`self:manage`) and exactly one
 * write about the installation's data: `alert:acknowledge`, marking an alert as seen. It holds no
 * verb that changes a person, a pass, a movement, a serving, an account or a setting, none that
 * takes data away in bulk (no `export:*`, no `person:export`), and none that puts a live bearer
 * token on a screen (`credential:reveal`). `packages/contracts/src/__tests__/permissions.test.ts` pins that
 * shape verb by verb and the server pins it route by route, so a row widened by accident is a red
 * gate rather than a quiet grant.
 *
 * **`analytics:read_security` is the dashboard's Security panel, and an analyst does not hold it.**
 * That panel is AD-37's meter: how many full records each account has opened today against the
 * exfiltration threshold, set beside the threshold itself. It is the oversight screen for the
 * reading roles, the analyst included, so it belongs to the people who oversee them. Every other
 * panel stays under `analytics:read`, which is why this one has a verb of its own rather than the
 * screen hiding a tab the route would still answer. **It is not secrecy, and nothing here claims
 * it is**: an analyst holding `audit:read` can count its own `person.view_full` rows in the trail
 * or read them on the Operators panel, and the threshold is a documented setting. The control on
 * an analyst's reading is detection, as for every role: the alert still fires, and the account it
 * is about can never mark it as seen.
 */
export const ROLE_GRANTS = {
    'person:read': ['operator', 'analyst', 'admin', 'super_admin'],
    'person:read_full': ['operator', 'analyst', 'admin', 'super_admin'],
    'person:write': ['operator', 'admin', 'super_admin'],
    'person:status': ['operator', 'admin', 'super_admin'],
    'person:shred': ['super_admin'],
    'person:export': ['admin', 'super_admin'],
    'person:import': ['super_admin'],
    'field:read': ['operator', 'analyst', 'admin', 'super_admin'],
    'field:write': ['super_admin'],
    'field:purge': ['super_admin'],
    'credential:read': ['operator', 'analyst', 'admin', 'super_admin'],
    'credential:reveal': ['admin', 'super_admin'],
    'credential:issue': ['operator', 'admin', 'super_admin'],
    'credential:revoke': ['operator', 'admin', 'super_admin'],
    'credential:print': ['operator', 'admin', 'super_admin'],
    'credential_batch:read': ['super_admin'],
    'credential_batch:write': ['super_admin'],
    'presence:read': ['operator', 'analyst', 'admin', 'super_admin'],
    'presence:submit': ['operator', 'admin', 'super_admin'],
    'presence:override': ['operator', 'admin', 'super_admin'],
    'presence:supervise': ['admin', 'super_admin'],
    'presence:backfill': ['admin', 'super_admin'],
    'presence:investigate': ['analyst', 'admin', 'super_admin'],
    'dining:serve': ['operator', 'admin', 'super_admin'],
    'dining:override': ['operator', 'admin', 'super_admin'],
    'dining:read': ['analyst', 'admin', 'super_admin'],
    'analytics:read': ['analyst', 'admin', 'super_admin'],
    'analytics:read_security': ['admin', 'super_admin'],
    'audit:read': ['analyst', 'admin', 'super_admin'],
    'alert:read': ['analyst', 'admin', 'super_admin'],
    'alert:acknowledge': ['analyst', 'admin', 'super_admin'],
    'user:read': ['analyst', 'admin', 'super_admin'],
    'user:write': ['admin', 'super_admin'],
    'user:reset_credentials': ['admin', 'super_admin'],
    'setting:read': ['super_admin'],
    'setting:write': ['super_admin'],
    'api_key:read': ['super_admin'],
    'api_key:write': ['super_admin'],
    'backup:read': ['super_admin'],
    'backup:run': ['super_admin'],
    'backup:restore': ['super_admin'],
    'backup:purge': ['super_admin'],
    'export:read': ['admin', 'super_admin'],
    'export:create': ['admin', 'super_admin'],
    'system:read': ['admin', 'super_admin'],
    'system:maintain': ['super_admin'],
    'self:manage': ['operator', 'analyst', 'admin', 'super_admin'],
};
/** Every role this system knows, as an array, for the matrix test's outer loop. */
export const ROLES = roleSchema.options;
/** Whether a role holds a verb. The only question the route layer of AD-36 ever asks. */
export function roleHasPermission(role, permission) {
    const holders = ROLE_GRANTS[permission];
    return holders.includes(role);
}
/** Every verb a role holds, sorted, for the generated documentation and for a readable failure. */
export function permissionsOf(role) {
    return PERMISSIONS.filter((permission) => roleHasPermission(role, permission));
}
//# sourceMappingURL=permissions.js.map